import React from "react";
import CircularProgress from "../../common/CircularProgress";
import InfoTooltip from "../../common/InfoTooltip";
import PatientLimitField from "./PatientLimitField";
import { normalizeForCompare } from "../../../utils/normalizeForCompare";

type TagItem = { name: string; active: boolean; pendingCert?: boolean; rejectedCert?: boolean };

interface ProfessionalData {
  services: TagItem[];
  themes: TagItem[];
  approaches: TagItem[];
}

/** Nombres (normalizados) recién dados de alta/baja, sin guardar todavía, en una categoría de chips. */
interface PendingChangeSet {
  added: Set<string>;
  removed: Set<string>;
}

interface ProfileAttentionTabProps {
  professionalData: ProfessionalData;
  toggleTag: (
    category: "services" | "themes" | "approaches",
    index: number,
    e: React.MouseEvent,
  ) => void;
  servicesLoading: boolean;
  loadingProfile: boolean;
  onSave: () => void;
  canSave: boolean;
  pendingAttentionChanges: {
    services: PendingChangeSet;
    themes: PendingChangeSet;
    approaches: PendingChangeSet;
  };
  patientLimit: {
    processLimit: number | null;
    draft: number | null;
    currentAssignedProcesses: number | null;
    onChange: (next: number) => void;
    isModified: boolean;
    psychologistId: string | null;
  };
  hidePatientLimit?: boolean;
}

/** Estilo de "cambio pendiente de guardar": anillo + relleno suave del mismo color, con texto
 *  legible en un tono oscuro — reemplaza temporalmente el relleno púrpura/gris del chip mientras
 *  el cambio no se guarda. El `!` fuerza la utilidad por sobre el color de `.tag-item--active/--inactive`
 *  (mismo patrón que `!bg-[#6D57BC]` ya usado en el botón Guardar Cambios de este archivo).
 *  Verde = alta pendiente, rojo = baja pendiente. */
function pendingIndicatorClass(name: string, pending: PendingChangeSet): string {
  const key = normalizeForCompare(name);
  if (pending.added.has(key)) return "ring-2 ring-green-500 ring-offset-1 !bg-green-50 !text-green-800";
  if (pending.removed.has(key)) return "ring-2 ring-red-500 ring-offset-1 !bg-red-50 !text-red-800";
  return "";
}

export const ProfileAttentionTab: React.FC<ProfileAttentionTabProps> = ({
  professionalData,
  toggleTag,
  servicesLoading,
  loadingProfile,
  onSave,
  canSave,
  pendingAttentionChanges,
  patientLimit,
  hidePatientLimit,
}) => {
  return (
    <div id="profile-attention-section">
      <div className="flex items-start gap-[0.4rem] mb-4 sm:mb-6">
        <h4 className="section-title !mb-0">Sobre tu atención</h4>
        <InfoTooltip
          ariaLabel="Información sobre tu atención"
          className="mt-1"
          text="Aquí verás los servicios, temáticas y enfoques que puedes ofrecer. Selecciona solo aquello en lo que te consideres con experiencia. Haz click para activar o desactivar cada opción y recuerda presionar 'Guardar Cambios' para aplicar las actualizaciones. Es importante mantener estas preferencias actualizadas, ya que recibirás pacientes de acuerdo con lo indicado."
        />
      </div>


      {/* Cantidad de pacientes */}
      {!hidePatientLimit && patientLimit.processLimit != null && patientLimit.draft != null && (
        <PatientLimitField
          value={patientLimit.draft}
          processLimit={patientLimit.processLimit}
          currentAssignedProcesses={patientLimit.currentAssignedProcesses}
          onChange={patientLimit.onChange}
          isModified={patientLimit.isModified}
          psychologistId={patientLimit.psychologistId}
          internalLimit={patientLimit.processLimit}
        />
      )}

      {/* Servicios */}
      <div className="tags-container">
        <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
          <p className="text-sm sm:text-base font-medium text-gray-700 m-0">Servicios</p>
          <InfoTooltip
            ariaLabel="Información sobre servicios"
            text="Los servicios definen el tipo de atención que brindas (por ejemplo, Individual, Pareja, Familia). Selecciona únicamente los que realmente ofreces. Algunas opciones pueden requerir certificación antes de hacerse visibles en la plataforma."
          />
        </div>
        <div className="tags-list">
          {servicesLoading || loadingProfile ? (
            <div className="w-full flex justify-center p-4">
              <CircularProgress sizeRem={2} color="#6D57BC" label="Cargando servicios..." />
            </div>
          ) : (
            professionalData.services.map((service, index) => (
              // Ocultar Supervisión, Material Digital y Talleres/charlas (se devuelve null
              // en vez de filtrar para no desalinear el index que usa toggleTag)
              ["Supervisión", "Material Digital", "Talleres/charlas"].includes(service.name) ? null : (
              <button
                key={index}
                className={`tag-item ${
                  service.active
                    ? "tag-item--active"
                    : service.pendingCert
                      ? "tag-item--pending"
                      : service.rejectedCert
                        ? "tag-item--rejected"
                        : "tag-item--inactive"
                } ${
                  service.pendingCert || service.rejectedCert
                    ? ""
                    : pendingIndicatorClass(service.name, pendingAttentionChanges.services)
                }`}
                onClick={(e) => toggleTag("services", index, e)}
                title={
                  service.pendingCert
                    ? `Ya enviaste la documentación para ${service.name}. Está pendiente de revisión por el equipo.`
                    : service.rejectedCert
                      ? `La documentación para ${service.name} fue rechazada. Hacé click para subir nueva.`
                      : undefined
                }
              >
                {service.name}
                {service.pendingCert  && <span className="ml-1 text-xs">⏳</span>}
                {service.rejectedCert && <span className="ml-1 text-xs">⚠️</span>}
              </button>
              )
            ))
          )}
        </div>
      </div>

      {/* Temáticas */}
      <div className="tags-container">
        <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
          <p className="text-sm sm:text-base font-medium text-gray-700 m-0">Temáticas</p>
          <InfoTooltip
            ariaLabel="Información sobre temáticas"
            text="Las temáticas representan áreas problemáticas o intereses de trabajo clínico (ansiedad, duelo, trauma, etc.). Indica aquellas en las que tienes formación y experiencia. Recuerda que 'Desarrollo personal' es obligatoria. Puedes guardar tus preferencias con solo esa temática activa."
          />
        </div>
        <div className="tags-list">
          {professionalData.themes.map((theme, index) => (
            <button
              key={index}
              className={`tag-item ${theme.active ? "tag-item--active" : "tag-item--inactive"} ${pendingIndicatorClass(theme.name, pendingAttentionChanges.themes)}`}
              onClick={(e) => toggleTag("themes", index, e)}
            >
              {theme.name}
            </button>
          ))}
        </div>
      </div>

      {/* Enfoques */}
      <div className="tags-container">
        <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
          <p className="text-sm sm:text-base font-medium text-gray-700 m-0">Enfoques</p>
          <InfoTooltip
            ariaLabel="Información sobre enfoques"
            text="Los enfoques corresponden a marcos teóricos o técnicas predominantes (TCC, Gestalt, EMDR, etc.). Selecciona al menos uno que describa tu práctica habitual. Puedes combinar varios, priorizando los que usas con mayor frecuencia."
          />
        </div>
        <div className="tags-list">
          {professionalData.approaches.map((approach, index) => (
            <button
              key={index}
              className={`tag-item ${approach.active ? "tag-item--active" : "tag-item--inactive"} ${pendingIndicatorClass(approach.name, pendingAttentionChanges.approaches)}`}
              onClick={(e) => toggleTag("approaches", index, e)}
            >
              {approach.name}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onSave}
        disabled={!canSave}
        className={`save-changes-button ${canSave ? '!bg-[#6D57BC] !cursor-pointer' : ''}`}
      >
        Guardar Cambios
      </button>
    </div>
  );
};

export default ProfileAttentionTab;
